export interface HealthCheckResult {
  service: string;
  status: 'healthy' | 'degraded' | 'down';
  responseTime: number;
  lastChecked: Date;
  details?: string;
  uptime?: string;
  version?: string;
  explanation?: string;
}

export interface SystemHealthData {
  api: HealthCheckResult;
  database: HealthCheckResult;
  website: HealthCheckResult;
  dashboard: HealthCheckResult;
  overall: 'healthy' | 'degraded' | 'down';
}

export interface HealthHistoryEntry {
  timestamp: Date;
  service: string;
  status: 'healthy' | 'degraded' | 'down';
  responseTime: number;
}

export interface Incident {
  id: string;
  service: string;
  status: 'ongoing' | 'resolved';
  startTime: Date;
  endTime?: Date;
  duration?: number;
  description: string;
}

export interface UptimeStats {
  service: string;
  uptimePercentage: number;
  totalChecks: number;
  healthyChecks: number;
  avgResponseTime: number;
  lastDowntime?: Date;
}

export interface PingResult {
  service: string;
  success: boolean;
  responseTime: number;
  statusCode?: number;
  error?: string;
  timestamp: Date;
}

export interface AlertConfig {
  enabled: boolean;
  email?: string;
  services: string[];
  notifyOnDown: boolean;
  notifyOnDegraded: boolean;
  notifyOnRecovery: boolean;
}

class SystemHealthService {
  private healthCache: SystemHealthData | null = null;
  private lastCheck: Date | null = null;
  private readonly CACHE_DURATION = 30000;
  private readonly REQUEST_TIMEOUT = 10000;
  
  // History tracking (stored in localStorage for persistence)
  private readonly HISTORY_KEY = 'systemHealthHistory';
  private readonly INCIDENTS_KEY = 'systemHealthIncidents';
  private readonly ALERTS_KEY = 'systemHealthAlerts';
  private readonly MAX_HISTORY_ENTRIES = 1000; // ~24 hours at 30s intervals

  private getApiBaseUrl(): string {
    const envUrl = (window as any).REACT_APP_API_URL || process.env.REACT_APP_API_URL;
    return envUrl || 'http://localhost:3002';
  }

  private formatUptime(seconds: number): string {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }

  // ============ HISTORY MANAGEMENT ============
  
  getHistory(): HealthHistoryEntry[] {
    try {
      const stored = localStorage.getItem(this.HISTORY_KEY);
      if (stored) {
        const history = JSON.parse(stored);
        return history.map((h: any) => ({ ...h, timestamp: new Date(h.timestamp) }));
      }
    } catch (e) { console.error('Error loading history:', e); }
    return [];
  }

  private saveHistory(history: HealthHistoryEntry[]): void {
    try {
      // Keep only last MAX_HISTORY_ENTRIES
      const trimmed = history.slice(-this.MAX_HISTORY_ENTRIES);
      localStorage.setItem(this.HISTORY_KEY, JSON.stringify(trimmed));
    } catch (e) { console.error('Error saving history:', e); }
  }

  private addToHistory(data: SystemHealthData): void {
    const history = this.getHistory();
    const timestamp = new Date();
    
    ['api', 'database', 'website', 'dashboard'].forEach(service => {
      const check = data[service as keyof SystemHealthData] as HealthCheckResult;
      history.push({
        timestamp,
        service,
        status: check.status,
        responseTime: check.responseTime
      });
    });
    
    this.saveHistory(history);
    this.checkForIncidents(data);
  }

  getHistoryForService(service: string, hours: number = 24): HealthHistoryEntry[] {
    const history = this.getHistory();
    const cutoff = new Date(Date.now() - hours * 60 * 60 * 1000);
    return history.filter(h => h.service === service && h.timestamp >= cutoff);
  }

  // ============ INCIDENT MANAGEMENT ============

  getIncidents(): Incident[] {
    try {
      const stored = localStorage.getItem(this.INCIDENTS_KEY);
      if (stored) {
        const incidents = JSON.parse(stored);
        return incidents.map((i: any) => ({
          ...i,
          startTime: new Date(i.startTime),
          endTime: i.endTime ? new Date(i.endTime) : undefined
        }));
      }
    } catch (e) { console.error('Error loading incidents:', e); }
    return [];
  }

  private saveIncidents(incidents: Incident[]): void {
    try {
      // Keep only last 100 incidents
      const trimmed = incidents.slice(-100);
      localStorage.setItem(this.INCIDENTS_KEY, JSON.stringify(trimmed));
    } catch (e) { console.error('Error saving incidents:', e); }
  }

  private checkForIncidents(data: SystemHealthData): void {
    const incidents = this.getIncidents();
    const now = new Date();
    
    ['api', 'database', 'website', 'dashboard'].forEach(serviceName => {
      const check = data[serviceName as keyof SystemHealthData] as HealthCheckResult;
      const ongoingIncident = incidents.find(i => i.service === serviceName && i.status === 'ongoing');
      
      if (check.status === 'down') {
        if (!ongoingIncident) {
          // New incident
          incidents.push({
            id: `${serviceName}-${Date.now()}`,
            service: serviceName,
            status: 'ongoing',
            startTime: now,
            description: check.explanation || `${check.service} is down`
          });
        }
      } else if (ongoingIncident) {
        // Resolve incident
        ongoingIncident.status = 'resolved';
        ongoingIncident.endTime = now;
        ongoingIncident.duration = Math.round((now.getTime() - ongoingIncident.startTime.getTime()) / 1000);
      }
    });
    
    this.saveIncidents(incidents);
  }

  // ============ UPTIME STATS ============

  getUptimeStats(hours: number = 24): UptimeStats[] {
    const services = ['api', 'database', 'website', 'dashboard'];
    const serviceNames: Record<string, string> = {
      api: 'API Server',
      database: 'Database (MongoDB)',
      website: 'Website',
      dashboard: 'Dashboard'
    };
    
    return services.map(service => {
      const history = this.getHistoryForService(service, hours);
      const totalChecks = history.length;
      const healthyChecks = history.filter(h => h.status === 'healthy').length;
      const avgResponseTime = totalChecks > 0 
        ? Math.round(history.reduce((sum, h) => sum + h.responseTime, 0) / totalChecks)
        : 0;
      
      const downEntries = history.filter(h => h.status === 'down');
      const lastDowntime = downEntries.length > 0 
        ? downEntries[downEntries.length - 1].timestamp 
        : undefined;
      
      return {
        service: serviceNames[service] || service,
        uptimePercentage: totalChecks > 0 ? Math.round((healthyChecks / totalChecks) * 10000) / 100 : 100,
        totalChecks,
        healthyChecks,
        avgResponseTime,
        lastDowntime
      };
    });
  }

  // ============ ALERT CONFIGURATION ============

  getAlertConfig(): AlertConfig {
    try {
      const stored = localStorage.getItem(this.ALERTS_KEY);
      if (stored) return JSON.parse(stored);
    } catch (e) { console.error('Error loading alerts:', e); }
    return {
      enabled: false,
      services: ['api', 'database', 'website', 'dashboard'],
      notifyOnDown: true,
      notifyOnDegraded: false,
      notifyOnRecovery: true
    };
  }

  saveAlertConfig(config: AlertConfig): void {
    try {
      localStorage.setItem(this.ALERTS_KEY, JSON.stringify(config));
    } catch (e) { console.error('Error saving alerts:', e); }
  }

  // ============ PING TEST ============

  async runPingTest(): Promise<PingResult[]> {
    const results: PingResult[] = [];
    const apiBaseUrl = this.getApiBaseUrl();
    const websiteUrl = window.location.origin.replace(':3001', ':3000').replace(':5173', ':3000');
    
    // Test API
    const apiResult = await this.pingEndpoint(apiBaseUrl + '/health', 'API Server');
    results.push(apiResult);
    
    // Test Database (via API)
    const dbResult = await this.pingEndpoint(apiBaseUrl + '/health', 'Database');
    results.push(dbResult);
    
    // Test Website
    const webResult = await this.pingWebsite(websiteUrl, 'Website');
    results.push(webResult);
    
    // Dashboard is always reachable if this code runs
    results.push({
      service: 'Dashboard',
      success: true,
      responseTime: 0,
      statusCode: 200,
      timestamp: new Date()
    });
    
    return results;
  }

  private async pingEndpoint(url: string, service: string): Promise<PingResult> {
    const startTime = performance.now();
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      return {
        service,
        success: response.ok,
        responseTime: Math.round(performance.now() - startTime),
        statusCode: response.status,
        timestamp: new Date()
      };
    } catch (error) {
      return {
        service,
        success: false,
        responseTime: Math.round(performance.now() - startTime),
        error: error instanceof Error ? error.message : 'Connection failed',
        timestamp: new Date()
      };
    }
  }

  private async pingWebsite(url: string, service: string): Promise<PingResult> {
    const startTime = performance.now();
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      await fetch(url, { method: 'HEAD', mode: 'no-cors', signal: controller.signal });
      clearTimeout(timeoutId);
      
      return {
        service,
        success: true,
        responseTime: Math.round(performance.now() - startTime),
        timestamp: new Date()
      };
    } catch (error) {
      return {
        service,
        success: false,
        responseTime: Math.round(performance.now() - startTime),
        error: error instanceof Error ? error.message : 'Connection failed',
        timestamp: new Date()
      };
    }
  }

  // ============ HEALTH CHECKS ============

  async checkAPIHealth(): Promise<HealthCheckResult> {
    const startTime = performance.now();
    const apiBaseUrl = this.getApiBaseUrl();
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.REQUEST_TIMEOUT);
      
      const response = await fetch(`${apiBaseUrl}/health`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      const responseTime = performance.now() - startTime;
      
      if (response.ok) {
        const data = await response.json();
        return {
          service: 'API Server',
          status: 'healthy',
          responseTime: Math.round(responseTime),
          lastChecked: new Date(),
          details: `Environment: ${data.environment || 'production'}`,
          uptime: this.formatUptime(data.uptime || 0),
          version: data.version || '1.0.0'
        };
      }
      
      return {
        service: 'API Server',
        status: 'degraded',
        responseTime: Math.round(responseTime),
        lastChecked: new Date(),
        details: `HTTP ${response.status}`,
        explanation: `API returned status ${response.status}. The server is running but may have issues.`
      };
    } catch (error) {
      const responseTime = performance.now() - startTime;
      const isTimeout = error instanceof Error && error.name === 'AbortError';
      
      return {
        service: 'API Server',
        status: 'down',
        responseTime: Math.round(responseTime),
        lastChecked: new Date(),
        details: isTimeout ? 'Request timeout' : 'Connection failed',
        explanation: isTimeout 
          ? 'Request timed out after 10 seconds.'
          : `Cannot connect to API at ${apiBaseUrl}. Make sure the API server is running.`
      };
    }
  }

  async checkDatabaseHealth(): Promise<HealthCheckResult> {
    const startTime = performance.now();
    const apiBaseUrl = this.getApiBaseUrl();
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.REQUEST_TIMEOUT);
      
      const response = await fetch(`${apiBaseUrl}/health`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      const responseTime = performance.now() - startTime;
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.database) {
          const dbHealth = data.database;
          const isHealthy = dbHealth.status === 'healthy' && dbHealth.connected === true;
          
          let details = dbHealth.message || 'Database check completed';
          if (dbHealth.database && dbHealth.collections !== undefined) {
            details = `${dbHealth.database} • ${dbHealth.collections} collections • ${dbHealth.dataSize || 'N/A'}`;
          }
          
          return {
            service: 'Database (MongoDB)',
            status: isHealthy ? 'healthy' : 'down',
            responseTime: dbHealth.responseTime || Math.round(responseTime),
            lastChecked: new Date(),
            details,
            explanation: isHealthy ? undefined : 'Database connection failed. Check MongoDB connection string.'
          };
        }
      }
      
      return {
        service: 'Database (MongoDB)',
        status: 'degraded',
        responseTime: Math.round(responseTime),
        lastChecked: new Date(),
        details: 'Unable to get database status',
        explanation: 'API is running but database health info is not available.'
      };
    } catch (error) {
      return {
        service: 'Database (MongoDB)',
        status: 'down',
        responseTime: Math.round(performance.now() - startTime),
        lastChecked: new Date(),
        details: 'Cannot check database',
        explanation: 'Cannot reach API server to check database status.'
      };
    }
  }

  async checkWebsiteHealth(): Promise<HealthCheckResult> {
    const startTime = performance.now();
    
    try {
      let websiteUrl = window.location.origin;
      websiteUrl = websiteUrl.replace(':3001', ':3000').replace(':5173', ':3000').replace(':5174', ':3000');
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.REQUEST_TIMEOUT);
      
      await fetch(websiteUrl, { method: 'HEAD', mode: 'no-cors', signal: controller.signal });
      clearTimeout(timeoutId);
      
      return {
        service: 'Website',
        status: 'healthy',
        responseTime: Math.round(performance.now() - startTime),
        lastChecked: new Date(),
        details: `Reachable at ${websiteUrl}`
      };
    } catch (error) {
      const responseTime = performance.now() - startTime;
      const isTimeout = error instanceof Error && error.name === 'AbortError';
      
      return {
        service: 'Website',
        status: 'down',
        responseTime: Math.round(responseTime),
        lastChecked: new Date(),
        details: isTimeout ? 'Request timeout' : 'Unreachable',
        explanation: 'Cannot connect to website. Make sure the website is running.'
      };
    }
  }

  async checkDashboardHealth(): Promise<HealthCheckResult> {
    let memoryInfo = 'Active';
    const perfMemory = (performance as any).memory;
    if (perfMemory && perfMemory.usedJSHeapSize) {
      const usedMB = (perfMemory.usedJSHeapSize / 1048576).toFixed(1);
      const totalMB = (perfMemory.totalJSHeapSize / 1048576).toFixed(1);
      memoryInfo = `Memory: ${usedMB}MB / ${totalMB}MB`;
    }
    
    return {
      service: 'Dashboard',
      status: 'healthy',
      responseTime: 0,
      lastChecked: new Date(),
      details: memoryInfo
    };
  }

  async getSystemHealth(forceRefresh = false): Promise<SystemHealthData> {
    if (!forceRefresh && this.healthCache && this.lastCheck) {
      const timeSinceCheck = Date.now() - this.lastCheck.getTime();
      if (timeSinceCheck < this.CACHE_DURATION) {
        return this.healthCache;
      }
    }

    const [api, database, website, dashboard] = await Promise.all([
      this.checkAPIHealth(),
      this.checkDatabaseHealth(),
      this.checkWebsiteHealth(),
      this.checkDashboardHealth()
    ]);

    const criticalStatuses = [api.status, database.status];
    const allStatuses = [api.status, database.status, website.status, dashboard.status];
    
    let overall: 'healthy' | 'degraded' | 'down';
    if (criticalStatuses.some(s => s === 'down')) {
      overall = 'down';
    } else if (allStatuses.every(s => s === 'healthy')) {
      overall = 'healthy';
    } else {
      overall = 'degraded';
    }

    const healthData: SystemHealthData = { api, database, website, dashboard, overall };
    
    this.healthCache = healthData;
    this.lastCheck = new Date();
    this.addToHistory(healthData);

    return healthData;
  }

  // ============ UTILITY METHODS ============

  getStatusColor(status: 'healthy' | 'degraded' | 'down'): string {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-100';
      case 'degraded': return 'text-yellow-600 bg-yellow-100';
      case 'down': return 'text-red-600 bg-red-100';
    }
  }

  getStatusIcon(status: 'healthy' | 'degraded' | 'down'): string {
    switch (status) {
      case 'healthy': return '✓';
      case 'degraded': return '⚠';
      case 'down': return '✗';
    }
  }

  getPerformanceRating(responseTime: number): string {
    if (responseTime < 100) return 'Excellent';
    if (responseTime < 300) return 'Good';
    if (responseTime < 500) return 'Fair';
    if (responseTime < 1000) return 'Slow';
    return 'Very Slow';
  }

  clearHistory(): void {
    localStorage.removeItem(this.HISTORY_KEY);
    localStorage.removeItem(this.INCIDENTS_KEY);
  }

  // Generate public status page data
  generateStatusPageData(): object {
    const healthData = this.healthCache;
    const uptimeStats = this.getUptimeStats(24);
    const incidents = this.getIncidents().filter(i => i.status === 'ongoing' || 
      (i.endTime && new Date().getTime() - i.endTime.getTime() < 24 * 60 * 60 * 1000));
    
    return {
      generatedAt: new Date().toISOString(),
      overall: healthData?.overall || 'unknown',
      services: uptimeStats.map(s => ({
        name: s.service,
        status: healthData ? 
          (healthData[s.service.toLowerCase().split(' ')[0] as keyof SystemHealthData] as HealthCheckResult)?.status || 'unknown' 
          : 'unknown',
        uptime: s.uptimePercentage,
        avgResponseTime: s.avgResponseTime
      })),
      recentIncidents: incidents.map(i => ({
        service: i.service,
        status: i.status,
        startTime: i.startTime.toISOString(),
        endTime: i.endTime?.toISOString(),
        duration: i.duration,
        description: i.description
      }))
    };
  }
}

export const systemHealthService = new SystemHealthService();
